import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { LocationDTO } from '@sales-center/common'

const OR_SEPARATOR = /\s+or\s+/

function parseQuery(query: string): string[] {
  return query
    .toLowerCase()
    .split(OR_SEPARATOR)
    .map((term) => term.trim())
    .filter(Boolean)
}

function matchesQuery(location: LocationDTO, orTerms: string[]): boolean {
  const haystack = [
    location.company,
    location.firstName,
    location.lastName,
    location.locationCode,
    location.phoneNumber,
    location.altPhoneNumber,
    location.mobilePhone,
    location.street1,
    location.street2,
    location.city,
    location.stateAbbreviation,
    location.postalCode,
    location.emailAddresses?.map((item) => item.emailAddress).join(' '),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return orTerms.some((term) => haystack.includes(term))
}

export function useFilterServiceLocations(serviceLocations: LocationDTO[]) {
  const [searchText, setSearchText] = useState('')
  const [query, setQuery] = useState('')

  const onChangeSearchText = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setSearchText(event.target.value),
    [],
  )

  const debouncedUpdateQuery = useMemo(
    () => debounce((value: string) => setQuery(value.trim()), 250),
    [],
  )

  useEffect(() => {
    debouncedUpdateQuery(searchText)

    return () => {
      debouncedUpdateQuery.cancel()
    }
  }, [searchText, debouncedUpdateQuery])

  const filteredLocations = useMemo(() => {
    const orTerms = parseQuery(query)

    return orTerms.length === 0
      ? serviceLocations
      : serviceLocations.filter((loc) => matchesQuery(loc, orTerms))
  }, [serviceLocations, query])

  return {
    searchText,
    onChangeSearchText,
    filteredLocations,
  }
}
